import { Alert, type ComboboxItem, Group, Select, Text } from '@mantine/core';
import { InfoIcon } from 'lucide-react';
import { type FC, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import styles from '@/components/flow-drawer/components/flow-drawer-comparison-item-select/flow-drawer-comparison-item-select.module.css';
import useFlowStore from '@/store/flow.ts';
import type { CanvasItemFrontend } from '@/types/common.ts';
import { editorValueToPlainText } from '@/utils/editor-conversion';

type CustomOption = ComboboxItem & { source: CanvasItemFrontend };

export const FlowDrawerComparisonItemSelect: FC = () => {
	const { library, sections, setCardToCompare, cardToCompare, flowItem } =
		useFlowStore(
			useShallow((state) => ({
				flowItem: state.flowDrawer.flowItem,
				library: state.library,
				sections: state.canvas.sections,
				cardToCompare: state.flowDrawer.cardToCompare,
				setCardToCompare: state.setCardToCompare
			}))
		);

	const canvasOptions = useMemo(() => {
		if (!flowItem?.type) {
			return [];
		}

		return sections[flowItem.type]
			.map((item) => ({
				label: `${editorValueToPlainText(item.title)} - CANVAS ITEM`,
				value: item.idn,
				source: item
			}))
			.sort((a, b) => {
				if (a.value === flowItem.source.idn) {
					return -1;
				}
				return b.value === flowItem.source.idn ? 1 : 0;
			});
	}, [sections, flowItem]);

	const libraryOptions = useMemo(() => {
		if (!library || !flowItem?.type) {
			return [];
		}
		return (
			library
				.find((item) => item.idn === flowItem.type)
				?.items.map((item) => ({
					label: `${editorValueToPlainText(item.title)} - LIBRARY ITEM`,
					value: `${item.idn}:library`,
					source: item
				})) ?? []
		);
	}, [library, flowItem?.type]);

	const handleSelectComparisonItem = useCallback(
		(_: string | null, option: ComboboxItem) => {
			setCardToCompare((option as CustomOption).source);
		},
		[setCardToCompare]
	);

	const isContentsIdentical = useMemo(() => {
		return (
			editorValueToPlainText(flowItem?.source?.title) ===
				editorValueToPlainText(cardToCompare?.title) &&
			editorValueToPlainText(flowItem?.source?.body) ===
				editorValueToPlainText(cardToCompare?.body)
		);
	}, [cardToCompare, flowItem]);

	return (
		<Group
			align="center"
			gap="sm"
			w="100%"
			wrap="nowrap"
		>
			<Text
				fz={13}
				style={{ whiteSpace: 'nowrap' }}
			>
				Compare with:
			</Text>

			<Select
				classNames={{
					input: styles.selectInput
				}}
				data={[
					{ group: 'CANVAS ITEMS', items: canvasOptions },
					{ group: 'LIBRARY ITEMS', items: libraryOptions }
				]}
				flex={1}
				miw={340}
				onChange={handleSelectComparisonItem}
				placeholder="Select Card to compare"
				searchable
				value={cardToCompare?.idn ?? ''}
			/>

			{isContentsIdentical && cardToCompare?.idn ? (
				<Alert
					color="green"
					icon={<InfoIcon />}
					p={6}
					title="Identical to the item on the right"
					variant="light"
				/>
			) : null}
		</Group>
	);
};
